import { Moon01, Sun } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { IconAction } from "@/components/app/IconAction";
import { useResolvedDark, useThemeStore } from "@/stores/themeStore";

/**
 * Jednoikonový přepínač motivu pro horní lištu: ikona ukazuje **aktuální** režim
 * (☾ tmavý / ☀ světlý) a klik ho převrátí.
 *
 * Tři volby včetně „Systém" tady záměrně nejsou — plné nastavení je v Nastavení →
 * Vzhled a v menu účtu ({@link ThemeToggle}). V liště jde o rychlé přehození, ne
 * o konfiguraci, a segmentový přepínač by ji zbytečně roztahoval.
 *
 * Při režimu „Systém" se ikona řídí tím, co je opravdu vidět
 * ({@link useResolvedDark}), takže klik z něj udělá explicitní volbu opačného
 * motivu — to je i jediný způsob, jak se z „Systému" dostat jedním kliknutím.
 *
 * Na rozdíl od ostatních ikon v liště **svítí**: slunce teple žlutě, měsíc chladně
 * modře. Šedá jako u nastavení a zvonečku by z ní udělala další nenápadnou akci,
 * kdežto tady barva sama nese informaci, jaký režim je zapnutý — a rozsvícený
 * měsíček v noci je to, co člověk čeká.
 */
const LIT_ICON = {
  // `*:data-icon:` míří na ikonu uvnitř tlačítka (viz `base/buttons/button.tsx`);
  // hover variantu je nutné přebít taky, jinak by ikona při přejetí zešedla.
  //
  // Záměrně surová paleta, ne semantické `utility-*` tokeny: ty se v tmavém režimu
  // převracejí (`utility-blue-300` → `blue-700`), takže by měsíček na tmavém pozadí
  // ztmavl. Každá ikona se přitom ukazuje jen v jednom motivu, kontrast je tedy dán.
  dark: "*:data-icon:text-blue-300 hover:*:data-icon:text-blue-200",
  light: "*:data-icon:text-yellow-500 hover:*:data-icon:text-yellow-600",
} as const;

export function ThemeIconToggle() {
  const { t } = useTranslation();
  const dark = useResolvedDark();
  const setMode = useThemeStore((s) => s.setMode);

  return (
    <IconAction
      icon={dark ? Moon01 : Sun}
      label={dark ? t("theme.switchToLight") : t("theme.switchToDark")}
      onPress={() => setMode(dark ? "light" : "dark")}
      className={dark ? LIT_ICON.dark : LIT_ICON.light}
    />
  );
}
